import { Board, BoardType, getBoardType } from "@sportactics/board-editing";
import { DebouncedFunc } from "lodash-es";
import { create } from "zustand";

import { debounceWithTracking, Tracking } from "~utils";

import { useBoardFirestorePersistence } from "./use-board-firestore-persistence";

export function useDebouncedUpsertFirestore(): useDebouncedUpsertFirestoreReturn {
  const { upsertBoard } = useBoardFirestorePersistence();

  const { getDebounced } = useDebouncedFuncStore.getState();
  const debouncedUpsert = getDebounced(upsertBoard);
  const flush = (): Promise<void> | undefined => debouncedUpsert.flush();

  const { isDiff, setPrevBoardType } = usePrevBoardTypeStore.getState();
  const upsertWithTypeCheck = async (
    userId: string,
    board: Board,
  ): Promise<void> => {
    const boardType = getBoardType(board);
    if (isDiff(boardType)) await flush();
    setPrevBoardType(boardType);
    await debouncedUpsert(userId, board);
  };

  return {
    upsertBoard: upsertWithTypeCheck,
    hasPending: debouncedUpsert.hasPending,
    flush,
  };
}

export type useDebouncedUpsertFirestoreReturn = {
  upsertBoard: UpsertBoard;
  hasPending: () => boolean;
  flush: () => Promise<void> | undefined;
};

const useDebouncedFuncStore = create<DebouncedFuncStore>((set, get) => ({
  debounced: undefined,

  getDebounced: (upsertBoard: UpsertBoard) => {
    const existing = get().debounced;
    if (existing) return existing;

    const debouncedUpsert = debounceWithTracking(upsertBoard, debounceMs, {
      leading: true,
      maxWait: debounceMs,
    });

    set({ debounced: debouncedUpsert });
    return debouncedUpsert;
  },
}));

type DebouncedFuncStore = {
  debounced?: DebouncedUpsertBoard;
  getDebounced: (upsertBoard: UpsertBoard) => DebouncedUpsertBoard;
};

type DebouncedUpsertBoard = DebouncedFunc<UpsertBoard> & Tracking;

type UpsertBoard = (userId: string, board: Board) => Promise<void>;

const debounceMs = 10000;

const usePrevBoardTypeStore = create<PrevBoardTypeStore>((set, get) => ({
  prevBoardType: undefined,
  isDiff: (boardType: BoardType) =>
    get().prevBoardType !== undefined && get().prevBoardType !== boardType,
  setPrevBoardType: (boardType: BoardType) =>
    set(() => ({ prevBoardType: boardType })),
}));

type PrevBoardTypeStore = {
  prevBoardType?: BoardType;
  isDiff: (boardType: BoardType) => boolean;
  setPrevBoardType: (boardType: BoardType) => void;
};
